// skipcq: JS-W1028
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { doc, onSnapshot } from 'firebase/firestore';

import useReviewReactionMutation from '../../hooks/useReviewReactionMutation';
import { db } from '../../utils/firebase';
import {
  REACTION_EMOJI_BY_TYPE,
  REACTION_TYPES,
  isReactionType,
  parseReactionSummary,
} from '../../utils/reviewReactionTypes';
import {
  trackPintPortfolioOpenReviewReactions,
  trackPintPortfolioReactReview,
  trackReviewDetailsReactReview,
} from '../../utils/brewhousePortalAnalytics';

const REVIEW_COLLECTION_BY_TYPE = {
  beer: 'beerReview',
  hospitality: 'hospitalityReview',
  event: 'eventReview',
};

function buildReactionDocId(reviewType, reviewId, actorType, actorId) {
  return `${reviewType}_${reviewId}_${actorType}_${actorId}`;
}

function ReactionOptionButton({ reactionType, selected, onSelect }) {
  const handleClick = useCallback(() => {
    onSelect(reactionType);
  }, [onSelect, reactionType]);

  return (
    <IconButton
      aria-label={`React with ${reactionType}`}
      onClick={handleClick}
      sx={{
        bgcolor: selected ? 'action.selected' : 'transparent',
      }}
    >
      <Typography component="span" sx={{ fontSize: 22 }}>
        {REACTION_EMOJI_BY_TYPE[reactionType]}
      </Typography>
    </IconButton>
  );
}

ReactionOptionButton.propTypes = {
  reactionType: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

function ReviewReactionDock({
  breweryId,
  beerId,
  review,
  reviewType = 'beer',
  enabled = true,
  analyticsSource = 'pint-portfolio',
  itemType = 'beer',
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewerReactionType, setViewerReactionType] = useState(null);
  const [reactionSummary, setReactionSummary] = useState(parseReactionSummary(review?.reactionSummary));
  const { mutate, loading, error } = useReviewReactionMutation();

  const reactionDocId = useMemo(
    () => buildReactionDocId(reviewType, review.id, 'brewery', breweryId),
    [breweryId, review.id, reviewType],
  );

  const reviewCollection = REVIEW_COLLECTION_BY_TYPE[reviewType] ?? 'beerReview';

  useEffect(() => {
    if (!enabled || !review?.id) {
      return undefined;
    }
    const reactionRef = doc(db, 'reviewReaction', reactionDocId);
    const reviewRef = doc(db, reviewCollection, review.id);
    const unsubscribeReaction = onSnapshot(reactionRef, (snapshot) => {
      const data = snapshot.data();
      setViewerReactionType(isReactionType(data?.reactionType) ? data.reactionType : null);
    });
    const unsubscribeReview = onSnapshot(reviewRef, (snapshot) => {
      setReactionSummary(parseReactionSummary(snapshot.data()?.reactionSummary));
    });
    return () => {
      unsubscribeReaction();
      unsubscribeReview();
    };
  }, [enabled, reactionDocId, review.id, reviewCollection]);

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    try {
      if (analyticsSource !== 'review-details') {
        trackPintPortfolioOpenReviewReactions({ breweryId, beerId, reviewId: review.id });
      }
    } catch {
      // Analytics must not block UI.
    }
  }, [analyticsSource, beerId, breweryId, review.id]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelectReaction = useCallback(async (reactionType) => {
    handleClose();
    try {
      if (analyticsSource === 'review-details') {
        trackReviewDetailsReactReview({
          breweryId,
          reviewType,
          reviewId: review.id,
          itemType,
          reactionType,
        });
      } else {
        trackPintPortfolioReactReview({
          breweryId,
          beerId,
          reviewId: review.id,
          reactionType,
        });
      }
    } catch {
      // Analytics must not block mutation.
    }
    try {
      await mutate({
        reviewType,
        reviewId: review.id,
        reactionType,
        actorType: 'brewery',
        actorId: breweryId,
      });
    } catch {
      // Error state from the hook drives the inline Alert.
    }
  }, [
    analyticsSource,
    beerId,
    breweryId,
    handleClose,
    itemType,
    mutate,
    review.id,
    reviewType,
  ]);

  if (!enabled) {
    return null;
  }

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton
          size="small"
          aria-label="React to review as brewery"
          onClick={handleOpen}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={18} />
          ) : (
            <Typography component="span" sx={{ fontSize: 20 }}>
              {viewerReactionType ? REACTION_EMOJI_BY_TYPE[viewerReactionType] : '👍'}
            </Typography>
          )}
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          {reactionSummary.total}
        </Typography>
        <Stack direction="row" spacing={0.25} sx={{ ml: 'auto' }}>
          {reactionSummary.topReactionTypes.map((reactionTypeKey) => (
            <Typography key={reactionTypeKey} component="span" sx={{ fontSize: 16 }}>
              {REACTION_EMOJI_BY_TYPE[reactionTypeKey]}
            </Typography>
          ))}
        </Stack>
      </Stack>
      {error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          Could not update reaction. Please try again.
        </Alert>
      ) : null}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack direction="row" spacing={0.5} sx={{ p: 1 }}>
          {REACTION_TYPES.map((reactionTypeKey) => (
            <ReactionOptionButton
              key={reactionTypeKey}
              reactionType={reactionTypeKey}
              selected={viewerReactionType === reactionTypeKey}
              onSelect={handleSelectReaction}
            />
          ))}
        </Stack>
      </Popover>
    </Box>
  );
}

ReviewReactionDock.propTypes = {
  breweryId: PropTypes.string.isRequired,
  beerId: PropTypes.string,
  review: PropTypes.shape({
    id: PropTypes.string.isRequired,
    reactionSummary: PropTypes.object,
  }).isRequired,
  reviewType: PropTypes.oneOf(['beer', 'hospitality', 'event']),
  enabled: PropTypes.bool,
  analyticsSource: PropTypes.oneOf(['pint-portfolio', 'review-details']),
  itemType: PropTypes.oneOf(['beer', 'brewery']),
};

export default ReviewReactionDock;
